/**
 * Global timezone store — the ONE source of the zone every timestamp in the
 * UI is rendered, parsed and bucketed in. The `useTimezone` / `useFormatDate`
 * hooks subscribe to it; the `TimezoneProvider` fills it from `/api/profile`.
 *
 * Resolution order (org first, then the user may override — owner decision
 * 2026-10-15; see {@link resolveTimezone}):
 *   1. a zone the user explicitly chose (source 'user')
 *   2. the organization default zone    (source 'org')
 *   3. the browser-detected zone        (source 'detected')
 *   4. UTC                              (source 'utc')
 *
 * Why a store and not just React Context: lots of code paths render
 * timestamps from non-React contexts too (DataTable cell formatters,
 * chart label callbacks, dayjs-using utilities, Date#toLocaleString —
 * see installZonedDateLocale). They call {@link getCurrentTz} directly.
 *
 * Starts on the browser zone so cold-load doesn't flash "UTC" before the
 * profile API resolves; the source is 'detected' until then, which is the
 * truth.
 */
import { resolveTimezone, browserTimezone } from './resolveTimezone';

let userTz = null;        // the user's own zone (null = inherit)
let orgDefaultTz = null;  // the organization default (null = none)
let currentTz = browserTimezone();
let currentSource = 'detected';
let currentLocale = (typeof navigator !== 'undefined' && navigator.language) || 'en-US';
let currentTimeFormat = '24h'; // '24h' | '12h'

const subscribers = new Set();
const notify = () => subscribers.forEach((fn) => fn());

export const getCurrentTz = () => currentTz;
/** 'user' | 'org' | 'detected' — where {@link getCurrentTz} came from. */
export const getTzSource = () => currentSource;
export const getOrgDefaultTz = () => orgDefaultTz;
export const getUserTz = () => userTz;
export const getCurrentLocale = () => currentLocale;
export const getCurrentTimeFormat = () => currentTimeFormat;

/** Re-resolve from the stored inputs; returns true when anything changed. */
const reresolve = () => {
  const { tz, source } = resolveTimezone({ userTz, orgTz: orgDefaultTz });
  if (tz === currentTz && source === currentSource) return false;
  currentTz = tz;
  currentSource = source;
  return true;
};

/**
 * Set the user's OWN zone (the profile selector). `null`/'' = inherit the
 * organization default, then the browser.
 */
export const setCurrentTz = (tz) => {
  userTz = tz && typeof tz === 'string' ? tz : null;
  if (reresolve()) notify();
};

/** Set the organization default (the Organization settings page). */
export const setOrgDefaultTz = (tz) => {
  orgDefaultTz = tz && typeof tz === 'string' ? tz : null;
  if (reresolve()) notify();
};

export const setCurrentLocale = (locale) => {
  const next = locale && typeof locale === 'string' ? locale : 'en-US';
  if (next === currentLocale) return;
  currentLocale = next;
  notify();
};

export const setCurrentTimeFormat = (fmt) => {
  const next = fmt === '12h' ? '12h' : '24h';
  if (next === currentTimeFormat) return;
  currentTimeFormat = next;
  notify();
};

/**
 * Apply a `/api/profile` response in one notification.
 *
 * Reads `profile.timezone` (the user's own zone, users.timezone) and
 * `profile.orgDefaultTimezone`. Falls back to `preferences.timezone` only when
 * the backend predates `profile.timezone` (field absent), so a frontend that
 * ships first still works.
 */
export const setFromProfile = (profile) => {
  if (!profile) return;
  const prefs = profile.preferences || {};
  const own = Object.prototype.hasOwnProperty.call(profile, 'timezone')
    ? profile.timezone
    : prefs.timezone;
  userTz = own && typeof own === 'string' ? own : null;
  orgDefaultTz = profile.orgDefaultTimezone && typeof profile.orgDefaultTimezone === 'string'
    ? profile.orgDefaultTimezone : null;
  let changed = reresolve();
  const locNext = prefs.locale && typeof prefs.locale === 'string' ? prefs.locale : 'en-US';
  const tfNext = prefs.timeFormat === '12h' ? '12h' : '24h';
  if (locNext !== currentLocale) { currentLocale = locNext; changed = true; }
  if (tfNext !== currentTimeFormat) { currentTimeFormat = tfNext; changed = true; }
  if (changed) notify();
};

/**
 * Legacy bulk update (preferences object + optional org zone). Kept for
 * callers that only have preferences in hand. `orgTz` undefined = keep the
 * org default already known.
 */
export const setFromPreferences = (prefs, orgTz) => {
  if (!prefs) return;
  const profile = {
    preferences: prefs,
    timezone: prefs.timezone || null,
    orgDefaultTimezone: orgTz !== undefined ? orgTz : orgDefaultTz,
  };
  setFromProfile(profile);
};

/** {@link useSyncExternalStore}'s subscribe contract. */
export const subscribe = (fn) => {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
};

/** Combined snapshot so a tz-, source-, locale- or format-only change re-renders. */
export const getSnapshot = () =>
  `${currentTz}|${currentSource}|${currentLocale}|${currentTimeFormat}`;

/** Test-only: reset to the cold-start state. */
export const __resetForTests = () => {
  userTz = null;
  orgDefaultTz = null;
  currentTz = browserTimezone();
  currentSource = 'detected';
  currentLocale = 'en-US';
  currentTimeFormat = '24h';
  notify();
};
